import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  text?: string;
  variant?: 'default' | 'magical' | 'minimal';
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'medium', 
  text,
  variant = 'default'
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'h-4 w-4';
      case 'large':
        return 'h-12 w-12';
      default:
        return 'h-8 w-8';
    }
  };

  const getTextSize = () => {
    switch (size) {
      case 'small':
        return 'text-sm';
      case 'large':
        return 'text-lg';
      default:
        return 'text-base';
    }
  };

  if (variant === 'minimal') {
    return (
      <div className={`animate-spin rounded-full border-2 border-gray-200 border-t-primary-500 ${getSizeClasses()}`} />
    );
  }

  if (variant === 'magical') {
    return (
      <div className="flex flex-col items-center space-y-3">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="relative"
        >
          <div className={`rounded-full bg-gradient-to-r from-magical-purple to-magical-pink p-3 ${getSizeClasses()}`}>
            <Sparkles className="h-full w-full text-white" />
          </div>
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 rounded-full bg-magical-purple opacity-20 blur-md"
          />
        </motion.div>
        
        {text && (
          <motion.p
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className={`font-children text-magical-purple ${getTextSize()}`}
          >
            {text}
          </motion.p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-3">
      <div className="relative">
        {/* Spinner principal */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className={`border-4 border-primary-100 border-t-primary-500 rounded-full ${getSizeClasses()}`}
        />
        
        {/* Effet de lueur */}
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className={`absolute inset-0 border-4 border-primary-200 rounded-full ${getSizeClasses()}`}
        />
      </div>
      
      {text && (
        <motion.p
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className={`text-gray-600 font-children text-center ${getTextSize()}`}
        >
          {text}
        </motion.p>
      )}
    </div>
  );
};

// Composant pour les états de chargement de page complète
export const PageLoader: React.FC<{ text?: string }> = ({ text = "Chargement..." }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
      <div className="text-center">
        <LoadingSpinner size="large" variant="magical" text={text} />
      </div>
    </div>
  );
};

// Hook pour les états de chargement avec timeout
export const useLoadingState = (initialState = false) => {
  const [isLoading, setIsLoading] = React.useState(initialState);
  
  const startLoading = () => setIsLoading(true);
  const stopLoading = () => setIsLoading(false);
  
  // Auto-stop après un délai (sécurité)
  React.useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        console.warn('Loading state auto-stopped after 30 seconds');
        setIsLoading(false);
      }, 30000);
      
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);
  
  return { isLoading, startLoading, stopLoading };
};
